/**
 * Order Booking Creatives — designer panel configuration.
 *
 * Single source of truth for the status model, sub-types, tabs and endpoint
 * paths described in the shared API contract. Nothing in this folder should
 * hard-code a status number or an endpoint string.
 */

export const STATUS = {
  1: { value: 1, key: 'draft', label: 'Draft', pill: 'bg-slate-100 text-slate-600 ring-slate-200' },
  2: { value: 2, key: 'assigned', label: 'Assigned', pill: 'bg-amber-50 text-amber-700 ring-amber-200' },
  3: { value: 3, key: 'picked', label: 'In Progress', pill: 'bg-sky-50 text-sky-700 ring-sky-200' },
  4: { value: 4, key: 'submitted', label: 'Design Submitted', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  5: { value: 5, key: 'rejected', label: 'Rejected', pill: 'bg-rose-50 text-rose-700 ring-rose-200' },
  6: { value: 6, key: 'final', label: 'Final', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  7: { value: 7, key: 'ready_to_live', label: 'Ready to Live', pill: 'bg-teal-50 text-teal-700 ring-teal-200' },
  8: { value: 8, key: 'order_created', label: 'Order Created', pill: 'bg-violet-50 text-violet-700 ring-violet-200' },
};

export const UNKNOWN_STATUS = {
  value: 0,
  key: 'unknown',
  label: 'Unknown',
  pill: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const getStatus = (status) => STATUS[Number(status)] || UNKNOWN_STATUS;

export const SUB_TYPE_LABELS = {
  full_image: 'Full Image',
  full_video: 'Full Video',
  promoted_image_story: 'Promoted Image Story',
  promoted_video_story: 'Promoted Video Story',
};

export const VIDEO_SUB_TYPES = ['full_video', 'promoted_video_story'];

export const PROMOTED_SUB_TYPES = ['promoted_image_story', 'promoted_video_story'];

export const SUB_TYPE_OPTIONS = Object.keys(SUB_TYPE_LABELS).map((value) => ({
  value,
  label: SUB_TYPE_LABELS[value],
}));

export const TABS = [
  { key: 'assigned', label: 'Assigned', status: 2 },
  { key: 'picked', label: 'In Progress', status: 3 },
  { key: 'submitted', label: 'Submitted', status: 4 },
  { key: 'rejected', label: 'Rejected', status: 5 },
  { key: 'approved', label: 'Approved', status: 6 },
];

export const TAB_KEYS = TABS.map((t) => t.key);

export const DEFAULT_TAB = 'assigned';

export const getTab = (key) => TABS.find((t) => t.key === key) || TABS[0];

/** Short, tab-specific empty-state copy (never a generic "No data"). */
export const TAB_EMPTY_COPY = {
  assigned: {
    title: 'Nothing waiting on you',
    line: 'New creatives assigned to you by sales will land here. Pick one to start designing.',
  },
  picked: {
    title: 'No designs in progress',
    line: 'Pick a creative from the Assigned tab and it will move here while you work on it.',
  },
  submitted: {
    title: 'Nothing submitted yet',
    line: 'Once you upload a design it moves here and waits for the sales review.',
  },
  rejected: {
    title: 'No rework requested',
    line: 'Creatives sent back with change requests will show up here.',
  },
  approved: {
    title: 'No approved designs yet',
    line: 'Approved creatives are collected here once sales signs off on them.',
  },
};

export const ENDPOINTS = {
  counts: '/user/booking-designer/counts',
  list: '/user/booking-designer/list',
  detail: '/user/booking-designer/detail',
  timeline: '/user/booking-designer/timeline',
  versions: '/user/booking-designer/versions',
  pick: '/user/booking-designer/pick',
  message: '/user/booking-designer/message',
  uploadImage: '/user/booking-designer/upload-image',
  uploadVideo: '/user/booking-designer/upload-video',
  uploadAttachment: '/user/booking-designer/upload-attachment',
};

export const PAGE_SIZE = 12;

/** localStorage key holding `{ tab, filters }`. */
export const STORAGE_KEY = 'ob_designer_filters';

/* ------------------------------------------------------------------ */
/* Permissions — mirrors the status/action matrix in the shared spec.  */
/* ------------------------------------------------------------------ */

export const canPick = (status) => Number(status) === 2;

export const canUpload = (status) => [3, 4, 5].includes(Number(status));

export const canMessage = (status) => [2, 3, 4, 5].includes(Number(status));

export const isVideoSubType = (subType) => VIDEO_SUB_TYPES.includes(String(subType || ''));

export const isPromotedSubType = (subType) => PROMOTED_SUB_TYPES.includes(String(subType || ''));

export const subTypeLabel = (subType) => SUB_TYPE_LABELS[subType] || subType || '—';

/* ------------------------------------------------------------------ */
/* Formatting helpers — every timestamp on the wire is epoch seconds.  */
/* ------------------------------------------------------------------ */

const toDate = (sec) => {
  const n = Number(sec);
  if (!n || Number.isNaN(n) || n <= 0) return null;
  return new Date(n * 1000);
};

export const formatEpoch = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatEpochDate = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export const formatEpochTime = (sec) => {
  const d = toDate(sec);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

/** "just now" / "4m" / "3h" / "6d" / falls back to a date past 30 days. */
export const formatRelative = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 0) return 'just now';
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return formatEpochDate(sec);
};

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i;
const VIDEO_RE = /\.(mp4|webm|ogg|mov|avi|m4v)(\?.*)?$/i;

export const looksLikeImage = (url) => IMAGE_RE.test(String(url || ''));
export const looksLikeVideo = (url) => VIDEO_RE.test(String(url || ''));

/** Normalised, stable shape for the filter bar — also the localStorage shape. */
export const EMPTY_FILTERS = {
  query: '',
  sub_type: '',
  start_date: '',
  end_date: '',
  sort_by_latest_activity: false,
};

// NOTE: `query` is deliberately NOT trimmed here — trimming a committed value
// would yank a trailing space back out of the search box while the user types.
// The trim happens once, at the API boundary.
export const normaliseFilters = (filters) => ({
  query: String(filters?.query || ''),
  sub_type: filters?.sub_type || '',
  start_date: filters?.start_date || '',
  end_date: filters?.end_date || '',
  sort_by_latest_activity: !!filters?.sort_by_latest_activity,
});

/**
 * Primitive, order-stable key for a filter object. Effects depend on THIS,
 * never on the object itself — object literals in deps re-run every render.
 */
export const filterKeyOf = (filters) => JSON.stringify(normaliseFilters(filters));

export const hasActiveFilters = (filters) => {
  const f = normaliseFilters(filters);
  return !!(
    f.query.trim() ||
    f.sub_type ||
    f.start_date ||
    f.end_date ||
    f.sort_by_latest_activity
  );
};
